/**
 * Colors are stored as straight (non-premultiplied) RGBA with components in
 * 0..1. The renderer premultiplies at instance-write time, since the swapchain
 * is configured `alphaMode: "premultiplied"`.
 */

export interface Color {
  r: number;
  g: number;
  b: number;
  a: number;
}

export function rgba(r: number, g: number, b: number, a = 1): Color {
  return { r, g, b, a };
}

/** From 0..255 byte components. */
export function rgb255(r: number, g: number, b: number, a = 255): Color {
  return { r: r / 255, g: g / 255, b: b / 255, a: a / 255 };
}

const HEX3 = /^#?([0-9a-f])([0-9a-f])([0-9a-f])([0-9a-f])?$/i;
const HEX6 = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})?$/i;

/** Parse "#rgb", "#rgba", "#rrggbb" or "#rrggbbaa". */
export function hex(value: string): Color {
  const short = HEX3.exec(value);
  if (short) {
    const [, r, g, b, a] = short;
    return rgb255(
      parseInt(r! + r!, 16),
      parseInt(g! + g!, 16),
      parseInt(b! + b!, 16),
      a ? parseInt(a + a, 16) : 255,
    );
  }
  const long = HEX6.exec(value);
  if (long) {
    const [, r, g, b, a] = long;
    return rgb255(
      parseInt(r!, 16),
      parseInt(g!, 16),
      parseInt(b!, 16),
      a ? parseInt(a, 16) : 255,
    );
  }
  throw new Error(`webwin: cannot parse color "${value}"`);
}

export function withAlpha(c: Color, a: number): Color {
  return { r: c.r, g: c.g, b: c.b, a };
}

/** Multiply alpha, e.g. fade a color to 50% of its current opacity. */
export function fade(c: Color, factor: number): Color {
  return { r: c.r, g: c.g, b: c.b, a: c.a * factor };
}

/** Linear blend in straight-alpha space; t=0 -> a, t=1 -> b. */
export function mixColor(a: Color, b: Color, t: number): Color {
  return {
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t,
    a: a.a + (b.a - a.a) * t,
  };
}

/** Blend toward white. */
export function lighten(c: Color, amount: number): Color {
  return mixColor(c, { r: 1, g: 1, b: 1, a: c.a }, amount);
}

/** Blend toward black. */
export function darken(c: Color, amount: number): Color {
  return mixColor(c, { r: 0, g: 0, b: 0, a: c.a }, amount);
}

/** Perceptual luminance (sRGB weights, no gamma decode) — used to pick ink color. */
export function luminance(c: Color): number {
  return 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b;
}

/**
 * A color as hue, saturation and value — the axes a person actually reaches
 * for when *choosing* a color, as opposed to the three the screen emits in.
 *
 * `h` is in degrees, `s`, `v` and `a` in 0..1. Kept as a separate type rather
 * than folded into `Color` because HSV is a picker's working space, not a
 * storage format: everything downstream of the picker wants RGBA.
 */
export interface Hsv {
  /** Degrees, 0..360. Zero for grays, which have no hue of their own. */
  h: number;
  s: number;
  v: number;
  a: number;
}

/**
 * RGB to HSV.
 *
 * Gray inputs report a hue of 0 because they genuinely have none — the
 * expression for it divides by the max-minus-min that is zero. A picker that
 * cares (and it should, or dragging a swatch to black would lose the hue the
 * user had dialled in) keeps the last real hue itself rather than reading it
 * back out of the color.
 */
export function rgbToHsv(c: Color): Hsv {
  const max = Math.max(c.r, c.g, c.b);
  const min = Math.min(c.r, c.g, c.b);
  const span = max - min;

  let h = 0;
  if (span > 0) {
    if (max === c.r) h = ((c.g - c.b) / span) % 6;
    else if (max === c.g) h = (c.b - c.r) / span + 2;
    else h = (c.r - c.g) / span + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: max === 0 ? 0 : span / max, v: max, a: c.a };
}

/** HSV back to RGB. `h` wraps; `s` and `v` are clamped. */
export function hsvToRgb(h: number, s: number, v: number, a = 1): Color {
  const hue = ((h % 360) + 360) % 360;
  const sat = Math.min(Math.max(s, 0), 1);
  const val = Math.min(Math.max(v, 0), 1);
  const chroma = val * sat;
  // The ramp within the 60-degree sector, and the gray the whole thing sits on.
  const ramp = chroma * (1 - Math.abs(((hue / 60) % 2) - 1));
  const base = val - chroma;

  switch (Math.floor(hue / 60) % 6) {
    case 0:
      return { r: chroma + base, g: ramp + base, b: base, a };
    case 1:
      return { r: ramp + base, g: chroma + base, b: base, a };
    case 2:
      return { r: base, g: chroma + base, b: ramp + base, a };
    case 3:
      return { r: base, g: ramp + base, b: chroma + base, a };
    case 4:
      return { r: ramp + base, g: base, b: chroma + base, a };
    default:
      return { r: chroma + base, g: base, b: ramp + base, a };
  }
}

/**
 * Back to "#rrggbb", or "#rrggbbaa" when the color is not opaque.
 *
 * The inverse of `hex`, for anywhere a color has to be shown to a person
 * or written down — a color picker's caption, a saved setting.
 */
export function toHex(c: Color): string {
  const byte = (v: number): string =>
    Math.round(Math.min(Math.max(v, 0), 1) * 255)
      .toString(16)
      .padStart(2, "0");
  const rgb = `#${byte(c.r)}${byte(c.g)}${byte(c.b)}`;
  return c.a >= 1 ? rgb : `${rgb}${byte(c.a)}`;
}

export function colorEquals(a: Color, b: Color): boolean {
  return a.r === b.r && a.g === b.g && a.b === b.b && a.a === b.a;
}

export const TRANSPARENT: Color = { r: 0, g: 0, b: 0, a: 0 };
export const BLACK: Color = { r: 0, g: 0, b: 0, a: 1 };
export const WHITE: Color = { r: 1, g: 1, b: 1, a: 1 };
